import Eventable from 'utils/eventable';
import utils from 'utils/utils';
import session, { SessionEntry } from './session';
import { SeekGesture } from './seek-gesture';
import type { PluginModel } from '../model';
import RelatedPlugin = jwplayer.RelatedPlugin;
import { StreamModel } from '../../model/stream';

type PlaybackEventsOptions = {
    vodInterval?: number;
    liveInterval?: number;
};

type Player = PluginModel['player'];

/**
 * Returns true when custom interval is set to false, null or 0
 */
function isCustomIntervalFalsy(customInterval?: number) {
    return customInterval !== undefined && !customInterval;
}

const queuePositions = new Map<string, number>();
const countedAssetIds = new Map<string, number>();

export type PlaybackEventData = {
    playMethod: string | null;
    previousAssetId?: number;
    playbackSource?: string;
    duration?: number;
    position?: number;
    queuePosition: number;
    loopCount: number;
};

/**
 * Parse SVP Player events and tailor data to pulse requirements
 *
 * Available events: Play, Replay, Pause, Resume, Stop, Watch
 */
class PlaybackEvents extends Eventable {
    player: Player;
    watchData: { time: number; duration: number };
    previousTime: number;
    beforeStreamStart?: boolean;
    playMethod: null | string;
    session: SessionEntry;
    vodInterval: number | undefined;
    liveInterval: number | undefined;
    liveTimeOffset: number | null = null;
    // TODO: `isSeeking` is redundant with `isPlaying` only for seeks that
    // happen during playback (isPlaying is already true there). For a seek
    // while paused, isPlaying is false, so isSeeking is the only guard
    // stopping the `play` JW fires after `seeked` from being reported as a
    // spurious Resume. Verify the paused-seek path before removing this.
    isSeeking: boolean = false;
    // Whether playback is logically active right now. Cleared only by the
    // three genuine playback-ending transitions (pause, complete,
    // playlistItem) - deliberately NOT by JW's `buffer`/stall-recovery
    // sequence, so a network stall can never look like a new play-type
    // event. This is distinct from `beforeStreamStart`, which tracks
    // whether content has never begun / has ended.
    isPlaying: boolean = false;
    loopCount: number;
    seekGesture: SeekGesture;

    constructor(player: Player, options: PlaybackEventsOptions) {
        super();
        this.player = player;
        this.watchData = {
            time: 0,
            duration: -1,
        };

        this.previousTime = -1;
        this.loopCount = 0;

        this.session = this.createSession()!;
        this.beforeStreamStart = true;
        this.playMethod = null;

        this.vodInterval = options?.vodInterval;
        this.liveInterval = options?.liveInterval;

        this.onBeforeUnload = this.onBeforeUnload.bind(this);
        this.onPlaylistItem = this.onPlaylistItem.bind(this);
        this.onAssetPlay = this.onAssetPlay.bind(this);
        this.onPause = this.onPause.bind(this);
        this.onPlay = this.onPlay.bind(this);
        this.onPlayNext = this.onPlayNext.bind(this);
        this.onLiveTime = this.onLiveTime.bind(this);
        this.onTime = this.onTime.bind(this);
        this.onComplete = this.onComplete.bind(this);
        this.onSeek = this.onSeek.bind(this);

        this.seekGesture = new SeekGesture(({ seekFromMs, seekToMs }) => {
            this.trigger('Seek', {
                ...this.eventData,
                position: seekToMs,
                prevPositionMs: seekFromMs,
                seekFromMs,
                seekToMs,
            });
        });

        this.listenTo(this.player, 'playlistItem', this.onPlaylistItem);
        this.listenTo(this.player, 'assetPlay', this.onAssetPlay);
        this.listenTo(this.player, 'pause', this.onPause);
        this.listenTo(this.player, 'play', this.onPlay);
        this.listenTo(this.player, 'playNext', this.onPlayNext);
        this.listenTo(this.player, 'seek', this.onSeek);
        this.listenTo(this.player, 'seeked', () => {
            setTimeout(() => {
                this.isSeeking = false;
            }, 0);
        });
        this.listenTo(this.player, 'scrubStart', () =>
            this.seekGesture.begin(),
        );
        this.listenTo(this.player, 'scrubEnd', () => this.seekGesture.flush());

        if (this.stream?.isLive()) {
            this.listenTo(this.player, 'time', this.onLiveTime);
        } else {
            this.listenTo(this.player, 'time', this.onTime);
        }

        this.listenTo(
            this.player,
            'RecommendedPlugin:nextAssetPlay',
            (
                _asset,
                mode: 'auto' | 'autoplayNext' | 'manualNext',
                opts: { source?: string },
            ) => {
                let playbackSource =
                    mode === 'auto' ? 'autoplayNext' : 'manualNext';
                if (opts.source && opts.source !== 'byId') {
                    playbackSource += `-${opts.source}`;
                }
                return this.onRecommendedPlay(playbackSource);
            },
        );

        this.listenTo(this.player, 'nextAutoAdvance', () =>
            this.onRecommendedPlay('autoplayNext-playlist'),
        );

        this.listenTo(this.player, 'nextClick', () =>
            this.onRecommendedPlay('manualNext-playlist'),
        );

        this.listenTo(this.player, 'relatedReady', () => {
            if (!this.jwRelatedPlugin) {
                return;
            }
            this.jwRelatedPlugin.on('feedClick', () => {
                this.beforeStreamStart = false;
                this.onComplete();
                this.onRecommendedPlay('manualNext-playlist');
            });
        });

        this.listenTo(this.player, 'playlistComplete', () => {
            this.listenToOnce(this.player, 'displayClick', () =>
                this.onRecommendedPlay('manualNext-playlist'),
            );
        });

        this.listenTo(this.player, 'RecommendedPlugin:playRecommended', () => {
            this.onRecommendedPlay('manualNext-grid');
        });

        this.listenToOnce(this.player, 'play', ({ playReason }) => {
            this.playMethod = playReason;
        });

        window.addEventListener(
            'onpagehide' in window.self ? 'pagehide' : 'beforeunload',
            this.onBeforeUnload,
            true,
        );
    }

    get queuePosition() {
        return queuePositions.get(this.player.sessionId) ?? 0;
    }

    get eventData() {
        const data: PlaybackEventData = {
            playMethod: this.playMethod,
            queuePosition: this.queuePosition,
            loopCount: this.loopCount,
        };
        const { previousAssetId } = this.session!;

        if (previousAssetId) {
            data.previousAssetId = previousAssetId;
            data.playbackSource = this.session!.playbackSource;
        }

        data.duration = this.watchData.duration * 1000;

        try {
            data.position = Math.floor(this.player.getCurrentTime()) * 1000;
        } catch {
            // could not parse time
            // do not set any value in this case
        }

        return data;
    }

    get stream(): StreamModel | null {
        try {
            return this.player.config.stream;
        } catch {
            // noop
        }
        return null;
    }

    get jw() {
        try {
            return this.player.getJWPlayer();
        } catch {
            // noop
        }
    }

    get jwRelatedPlugin() {
        try {
            return (this.jw?.getPlugin('related') as RelatedPlugin) || null;
        } catch {
            return null;
        }
    }

    getPosition() {
        try {
            return Math.floor(this.player.getCurrentTime());
        } catch {
            return 0;
        }
    }

    createSession() {
        // node is unique player identifier
        const node = this.player.config.get('node')! as { id: string } | string;
        const id = typeof node === 'string' ? node : node.id;

        /**
         * Initialize session object to keep reference
         */
        if (!session[id]) {
            session[id] = {};
        }

        return session[id];
    }

    trackTimeEvent({
        time,
        defaultInterval = 1,
        customInterval,
    }: {
        time: number;
        defaultInterval?: number;
        customInterval?: number;
    }) {
        // During a drag, suppress both the `Watch` event and the viewtime
        // accounting below - a scrub is not watched time. Consequence:
        // `watchData.duration` (-> `eventData.duration` -> the `viewtimeMs`
        // computed in tpaas.ts's onPlaybackEvent) under-reports on every
        // subsequent event that spreads `eventData`, not just the next
        // Watch. This is a correctness fix, not merely a preference:
        // without it, `time` jumps around during a scrub, so
        // `previousTime !== time` is true on nearly every tick and
        // `watchData.duration` would be grossly inflated instead.
        if (this.player.isScrubbing()) {
            return;
        }

        const eventInterval = utils.isNumber(customInterval)
            ? customInterval
            : defaultInterval;

        // track event data with one second interval. The very first tick
        // after a reset (previousTime === -1) only establishes the
        // baseline - it must not increment duration, otherwise duration
        // ends up permanently one second ahead of position (every
        // subsequent tick would keep that same +1 offset).
        if (time % 1 === 0 && this.previousTime !== time) {
            if (this.previousTime !== -1) {
                this.watchData.duration += 1;
            }
            this.previousTime = time;
        }

        if (
            eventInterval !== undefined &&
            time % eventInterval === 0 &&
            this.watchData.time !== time
        ) {
            this.watchData.time = time;

            // skip when custom interval is turned off
            if (isCustomIntervalFalsy(customInterval)) {
                return;
            }

            this.trigger('Watch', {
                ...this.eventData,
                prevPosition: this.previousTime * 1000,
            });
        }
    }

    onBeforeUnload() {
        if (this.beforeStreamStart === false) {
            this.onComplete();
        }
    }

    onPlaylistItem() {
        this.seekGesture.flush();
        this.isPlaying = false;

        const currentId = this.stream?.getId();
        if (
            currentId !== undefined &&
            !this.stream?.isMock() &&
            countedAssetIds.get(this.player.sessionId) !== currentId
        ) {
            countedAssetIds.set(this.player.sessionId, currentId);
            queuePositions.set(this.player.sessionId, this.queuePosition + 1);
        }

        this.listenToOnce(this.player, 'complete nextClick', this.onComplete);

        this.watchData.time = this.stream?.getPlaybackTime('begin') || 0;
        this.watchData.duration = 0;
        this.previousTime = -1;

        const track = this.trigger.bind(this, 'Load', this.eventData);

        /**
         * Only first playback has to wait for viewable event
         * Playnext/grid will work without it
         */
        if (this.session.wasViewed === true) {
            track();
        } else {
            // wait for player to be viewable
            this.listenTo(this.jw, 'viewable', (data) => {
                if (data.viewable) {
                    track();
                    this.stopListening(this.jw, 'viewable');
                    this.session.wasViewed = true;
                }
            });
        }
    }

    onAssetPlay() {
        const intent = this.beforeStreamStart ? 'Start' : 'Resume';
        this.beforeStreamStart = false;
        this.liveTimeOffset = null;

        this.watchData.duration = 0;
        this.previousTime = -1;
        this.trigger(intent, {
            ...this.eventData,
            start: true,
        });
        this.isPlaying = true;
    }

    onPause() {
        // suppressed while scrubbing: a pause JW emits mid-drag is not a
        // genuine pause and must produce no Pulse `Pause`
        if (this.player.isScrubbing()) {
            return;
        }

        this.trigger('Pause', this.eventData);
        this.isPlaying = false;
    }

    onPlay() {
        // play can't be triggered before assetPlay, if seeking, if
        // scrubbing, or if playback is already active (e.g. recovering
        // from a network stall)
        if (
            !this.beforeStreamStart &&
            !this.isSeeking &&
            !this.player.isScrubbing() &&
            !this.isPlaying
        ) {
            let eventName = this.loopCount > 1 ? 'Replay' : 'Start';
            if (this.eventData.position && this.eventData.position > 0) {
                eventName = 'Resume';
            }
            this.trigger(eventName, this.eventData);
        }

        const begin = (this.stream?.getPlaybackTime('begin') || 0) * 1000;
        if (this.eventData.position === begin) {
            this.loopCount += 1;
        }

        this.isPlaying = true;
    }

    /**
     * Intervals for VOD are defined as follows
     *
     * every 5s - during the first 10 seconds of content playback (no matter of where playback has started)
     * every 10s - for the range (10-60s) of content playback, after seeking intervals should be triggered in same time
     * every 30s - for the rest
     * seek 42, then first trigger should be in 50
     */
    onTime(eventTime: number) {
        const time = Math.floor(eventTime);
        let eventInterval;

        if (eventTime <= 10) {
            eventInterval = 5;
        } else if (eventTime <= 120) {
            eventInterval = 10;
        } else {
            eventInterval = 30;
        }
        this.trackTimeEvent({
            time,
            defaultInterval: eventInterval,
            customInterval: this.vodInterval,
        });
    }

    onSeek(data: {
        currentTime: number;
        duration: number;
        metadata: { currentTime: number };
        offset: number;
        position: number;
        seekRange: { start: number; end: number };
        type: 'seek';
    }) {
        this.isSeeking = true;

        if (this.player.isScrubbing()) {
            // accumulated; flushed once on scrubEnd
            this.seekGesture.push(data);
            return;
        }

        // Providers whose skin does not drive the scrubbing flag keep the
        // original behaviour: one Seek per seek, emitted when the seek
        // completes.
        //
        // Deliberately asymmetric with the gesture path above: the gesture
        // is always deterministically resolved by seekGesture.flush() in
        // onPlaylistItem/onComplete, but this one-shot `seeked` listener is
        // NOT torn down by those same handlers - it is cleared only by
        // destroy()'s stopListening(). That is intentional: it is exactly
        // the pre-fb22f1da behaviour that providers not using player-extras
        // still depend on, so tearing it down at asset boundaries would be
        // a behaviour change for them, not a cleanup.
        this.listenToOnce(this.player, 'seeked', () => {
            this.trigger('Seek', {
                ...this.eventData,
                position: Math.floor(data.offset * 1000),
                prevPositionMs: Math.floor(data.position * 1000),
                seekFromMs: Math.floor(data.position * 1000),
                seekToMs: Math.floor(data.offset * 1000),
            });
        });
    }

    /**
     * Track live events by checking time from flight time to current date
     * DVR seek is also included in calculations
     *
     * For no DVR streams jw.getPosition() is unreliable, so instead we
     * track elapsed watch time since the viewer joined (starting from 0),
     * anchored to broadcastElapsedTime at join via liveTimeOffset.
     */
    onLiveTime() {
        const duration = this.jw!.getDuration();
        const broadcastElapsedTime = Math.floor(
            (this.stream?.getTimeToStart() || 0) * -1,
        );

        let time;

        if (Number.isFinite(duration)) {
            // DVR: absolute position within the stream, DVR seeks included
            time = broadcastElapsedTime + this.jw!.getPosition();
        } else {
            // no DVR: elapsed watch time since the viewer joined
            if (this.liveTimeOffset === null) {
                this.liveTimeOffset = broadcastElapsedTime;
            }
            time = broadcastElapsedTime - this.liveTimeOffset;
        }

        // constant tracking interval
        this.trackTimeEvent({
            time: Math.floor(time),
            defaultInterval: 30,
            customInterval: this.liveInterval,
        });
    }

    onComplete(reason?: string) {
        if (reason === 'manualNext') {
            this.onRecommendedPlay('manualNext');
        }

        // flush any pending seek gesture now, so it is attributed to this
        // asset and emitted before Stop rather than lost or merged into
        // the next asset's playback
        this.seekGesture.flush();

        /**
         * Track stop event only when playback has started
         */
        if (this.beforeStreamStart === false) {
            this.trigger('Stop', this.eventData);
            // store asset id in memory to attach it to play next video
            this.session.previousAssetId = this.stream?.getId();
        }
        this.beforeStreamStart = true;
        this.isPlaying = false;
        this.loopCount = 0;
    }

    /**
     * @param {Number} _id
     * @param {Object} [options]
     * @param {Object} [options.pulse]
     * @param {String} [options.source] - A source that will be added as a playback source for pulse metrics
     */
    onPlayNext(
        _id: number,
        options: { pulse: Record<string, unknown>; source: string },
    ) {
        if (options && options.pulse) {
            Object.assign(this.session, options.pulse);
        }
    }

    onRecommendedPlay(playbackSource: string) {
        this.session.playbackSource = playbackSource;
    }

    destroy() {
        window.removeEventListener(
            'onpagehide' in window.self ? 'pagehide' : 'beforeunload',
            this.onBeforeUnload,
            true,
        );
        this.seekGesture.destroy();
        this.stopListening();
    }
}

export default PlaybackEvents;
